import { Link } from '@tanstack/react-router';

import { IndexNumber, Tag } from '@/components/editorial';
import { AppImage } from '@/components/layout/app-image';
import type { PublicProjectRecord } from '@/content/schemas';

import { isCaseStudyProject } from '../selectors';

type ProjectArchiveCardProps = {
  project: PublicProjectRecord;
  index: number;
};

function formatYear(publishedAt: string) {
  return new Date(publishedAt).getFullYear().toString();
}

export function ProjectArchiveCard({
  project,
  index,
}: ProjectArchiveCardProps) {
  const isCaseStudy = isCaseStudyProject(project);
  const cardBody = (
    <>
      <div className="bg-muted relative aspect-[16/10] md:aspect-auto md:min-h-[240px]">
        <AppImage
          src={project.hero.src}
          alt={project.hero.alt}
          fill
          className="object-cover transition-transform duration-500 group-hover:scale-[1.02]"
        />
      </div>

      <div className="flex flex-col gap-4 p-6 md:p-8">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="text-muted-foreground flex flex-wrap items-center gap-2 font-mono text-xs uppercase tracking-widest">
            <span>{isCaseStudy ? 'Case study' : 'Archive record'}</span>
            <span className="text-border">·</span>
            <span>{formatYear(project.publishedAt)}</span>
            {project.role && (
              <>
                <span className="text-border">·</span>
                <span>{project.role}</span>
              </>
            )}
          </div>
          <IndexNumber value={index} />
        </div>

        <div className="space-y-2">
          <h2 className="font-display text-ink group-hover:text-brand text-2xl font-semibold leading-snug transition-colors md:text-3xl">
            {project.title}
          </h2>
          <p className="text-muted-foreground text-sm leading-relaxed md:text-base">
            {project.description}
          </p>
        </div>

        {project.outcomes && project.outcomes.length > 0 && (
          <dl className="border-border grid grid-cols-2 gap-3 border-y py-4 sm:grid-cols-4">
            {project.outcomes.slice(0, 4).map((outcome) => (
              <div key={`${outcome.label}-${outcome.value}`}>
                <dt className="text-muted-foreground font-mono text-[10px] uppercase tracking-widest">
                  {outcome.label}
                </dt>
                <dd className="font-display text-ink text-sm font-semibold md:text-base">
                  {outcome.value}
                </dd>
              </div>
            ))}
          </dl>
        )}

        <div className="mt-auto flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap gap-1.5">
            {project.services.slice(0, 5).map((service) => (
              <Tag key={service} variant="muted">
                {service}
              </Tag>
            ))}
          </div>
          {isCaseStudy && project.route ? (
            <span className="text-brand font-mono text-xs font-medium">
              View case study →
            </span>
          ) : (
            <span className="text-muted-foreground font-mono text-xs">
              Index-only archive
            </span>
          )}
        </div>
      </div>
    </>
  );

  if (isCaseStudy && project.route) {
    return (
      <Link
        to={project.route}
        className="border-border hover:border-brand/50 active:border-brand/50 group grid overflow-hidden rounded-2xl border transition-colors active:scale-[0.99] md:grid-cols-[minmax(0,0.95fr)_minmax(0,1.05fr)]"
      >
        {cardBody}
      </Link>
    );
  }

  return (
    <article className="border-border grid overflow-hidden rounded-2xl border md:grid-cols-[minmax(0,0.95fr)_minmax(0,1.05fr)]">
      {cardBody}
    </article>
  );
}
